import { useState, useEffect, useCallback } from "react";
import {
  Plus,
  X,
  Loader2,
  Search,
  Send,
  CheckCircle2,
  XCircle,
  FileText,
  DollarSign,
  ExternalLink,
  Copy,
  RefreshCw,
  Filter,
  Ban,
} from "lucide-react";
import { colors, fonts } from "../styles/theme";
import { useTenant } from "../contexts/TenantContext";
import {
  fetchInvoices,
  createInvoice,
  sendInvoice,
  markInvoicePaid,
  voidInvoice,
  fetchContacts,
  fetchCatalog,
} from "../services/api";
import { Badge } from "../components/ui";

// ── Styles ────────────────────────────────────────────────────

const card = {
  background: colors.bgCard,
  border: `1px solid ${colors.border}`,
  borderRadius: 10,
  padding: 20,
};

const inputStyle = {
  width: "100%",
  padding: "8px 12px",
  fontSize: 13,
  fontFamily: fonts.body,
  background: colors.bg,
  border: `1px solid ${colors.border}`,
  borderRadius: 6,
  color: colors.text,
  outline: "none",
  boxSizing: "border-box",
};

const btnPrimary = {
  padding: "8px 18px",
  fontSize: 13,
  fontWeight: 600,
  fontFamily: fonts.body,
  background: colors.accent,
  color: "#ffffff",
  border: "none",
  borderRadius: 6,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
};

const btnSecondary = {
  ...btnPrimary,
  background: "transparent",
  color: colors.textSecondary,
  border: `1px solid ${colors.border}`,
};

const STATUS_CONFIG = {
  draft: {
    label: "Draft",
    color: colors.textSecondary,
    bg: `${colors.textSecondary}18`,
  },
  sent: { label: "Sent", color: "#3b82f6", bg: "rgba(59,130,246,0.1)" },
  paid: { label: "Paid", color: colors.success, bg: `${colors.success}18` },
  overdue: {
    label: "Overdue",
    color: colors.warning,
    bg: `${colors.warning}18`,
  },
  void: { label: "Void", color: colors.danger, bg: `${colors.danger}18` },
};

const FILTERS = [
  { value: "", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "sent", label: "Sent" },
  { value: "paid", label: "Paid" },
  { value: "overdue", label: "Overdue" },
  { value: "void", label: "Void" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function fmtCurrency(amount, currency = "USD") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(
    amount || 0,
  );
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return (
    <Badge color={cfg.color} bg={cfg.bg}>
      {cfg.label}
    </Badge>
  );
}

// ── Create Invoice Modal ──────────────────────────────────────

function CreateInvoiceModal({ open, onClose, onCreated, tenant }) {
  const [contactSearch, setContactSearch] = useState("");
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [products, setProducts] = useState([]);
  const [lineItems, setLineItems] = useState([
    { description: "", amount: "", quantity: 1, productId: null },
  ]);
  const [notes, setNotes] = useState("");
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date(Date.now() + 30 * 86400000);
    return d.toISOString().slice(0, 10);
  });
  const [syncToQBO, setSyncToQBO] = useState(true);
  const [createStripeLink, setCreateStripeLink] = useState(true);
  const [source, setSource] = useState("manual");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  // Load products on mount
  useEffect(() => {
    if (open) {
      fetchCatalog("product")
        .then((r) => setProducts(r.items || []))
        .catch(() => {});
    }
  }, [open]);

  // Search contacts
  useEffect(() => {
    if (!contactSearch || contactSearch.length < 2) {
      setContacts([]);
      return;
    }
    const t = setTimeout(() => {
      setSearchLoading(true);
      fetchContacts({ search: contactSearch, limit: 8 })
        .then((r) => setContacts(r.contacts || []))
        .catch(() => {})
        .finally(() => setSearchLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [contactSearch]);

  const addLineItem = () =>
    setLineItems((prev) => [
      ...prev,
      { description: "", amount: "", quantity: 1, productId: null },
    ]);
  const removeLineItem = (idx) =>
    setLineItems((prev) => prev.filter((_, i) => i !== idx));
  const updateLineItem = (idx, field, value) => {
    setLineItems((prev) =>
      prev.map((item, i) => (i === idx ? { ...item, [field]: value } : item)),
    );
  };

  const selectProduct = (idx, productId) => {
    const product = products.find((p) => p.id === productId);
    if (product) {
      updateLineItem(idx, "productId", product.id);
      updateLineItem(idx, "description", product.name);
      updateLineItem(idx, "amount", product.rackRate || 0);
    }
  };

  const totalAmount = lineItems.reduce(
    (sum, item) => sum + (parseFloat(item.amount) || 0) * (item.quantity || 1),
    0,
  );

  const handleSubmit = async () => {
    if (!selectedContact) {
      setError("Select a contact");
      return;
    }
    const validItems = lineItems.filter(
      (i) => i.description && parseFloat(i.amount) > 0,
    );
    if (!validItems.length) {
      setError("Add at least one line item");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const inv = await createInvoice({
        contactId: selectedContact.id,
        contactEmail: selectedContact.email,
        contactName:
          `${selectedContact.firstName || ""} ${selectedContact.lastName || ""}`.trim() ||
          selectedContact.email,
        companyName: selectedContact.company || "",
        items: validItems.map((i) => ({
          productId: i.productId,
          description: i.description,
          amount: parseFloat(i.amount),
          quantity: parseInt(i.quantity) || 1,
          currency: tenant.currency?.code || "USD",
        })),
        dueDate,
        notes,
        source,
        syncToQBO,
        createStripeLink,
        entitlementProducts: validItems
          .filter((i) => i.productId)
          .map((i) => i.productId),
      });
      onCreated(inv);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          background: colors.bgCard,
          borderRadius: 12,
          width: 620,
          maxHeight: "85vh",
          overflow: "auto",
          padding: 28,
          border: `1px solid ${colors.border}`,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <h2
            style={{
              fontFamily: fonts.heading,
              fontSize: 20,
              color: colors.text,
              margin: 0,
            }}
          >
            Create Invoice
          </h2>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: colors.textSecondary,
            }}
          >
            <X size={20} />
          </button>
        </div>

        {error && (
          <div
            style={{
              padding: "8px 12px",
              marginBottom: 12,
              borderRadius: 6,
              background: `${colors.danger}15`,
              color: colors.danger,
              fontSize: 12,
            }}
          >
            {error}
          </div>
        )}

        {/* Contact search */}
        <label
          style={{
            display: "block",
            fontSize: 12,
            fontWeight: 600,
            color: colors.textSecondary,
            marginBottom: 4,
          }}
        >
          Recipient
        </label>
        {selectedContact ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 12px",
              background: colors.bg,
              borderRadius: 6,
              marginBottom: 16,
              border: `1px solid ${colors.border}`,
            }}
          >
            <span style={{ fontSize: 13, color: colors.text, flex: 1 }}>
              {selectedContact.firstName} {selectedContact.lastName} —{" "}
              {selectedContact.email}
              {selectedContact.company && (
                <span style={{ color: colors.textSecondary }}>
                  {" "}
                  ({selectedContact.company})
                </span>
              )}
            </span>
            <button
              onClick={() => {
                setSelectedContact(null);
                setContactSearch("");
              }}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: colors.textSecondary,
              }}
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <div style={{ position: "relative", marginBottom: 16 }}>
            <input
              value={contactSearch}
              onChange={(e) => setContactSearch(e.target.value)}
              placeholder="Search contacts by name or email…"
              style={inputStyle}
            />
            {searchLoading && (
              <Loader2
                size={14}
                style={{
                  position: "absolute",
                  right: 10,
                  top: 10,
                  color: colors.textSecondary,
                }}
                className="spin"
              />
            )}
            {contacts.length > 0 && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  background: colors.bgCard,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 6,
                  maxHeight: 200,
                  overflow: "auto",
                  zIndex: 10,
                }}
              >
                {contacts.map((c) => (
                  <div
                    key={c.id}
                    onClick={() => {
                      setSelectedContact(c);
                      setContacts([]);
                      setContactSearch("");
                    }}
                    style={{
                      padding: "8px 12px",
                      fontSize: 13,
                      cursor: "pointer",
                      borderBottom: `1px solid ${colors.border}`,
                      color: colors.text,
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = colors.bg)
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "transparent")
                    }
                  >
                    {c.firstName} {c.lastName} — {c.email}
                    {c.company && (
                      <span
                        style={{ color: colors.textSecondary, marginLeft: 6 }}
                      >
                        ({c.company})
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Line items */}
        <label
          style={{
            display: "block",
            fontSize: 12,
            fontWeight: 600,
            color: colors.textSecondary,
            marginBottom: 8,
          }}
        >
          Line Items
        </label>
        {lineItems.map((item, idx) => (
          <div
            key={idx}
            style={{
              display: "flex",
              gap: 8,
              marginBottom: 8,
              alignItems: "center",
            }}
          >
            <div style={{ flex: 1 }}>
              {products.length > 0 ? (
                <select
                  value={item.productId || ""}
                  onChange={(e) =>
                    e.target.value
                      ? selectProduct(idx, e.target.value)
                      : updateLineItem(idx, "productId", null)
                  }
                  style={{ ...inputStyle, cursor: "pointer" }}
                >
                  <option value="">Custom item…</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} —{" "}
                      {fmtCurrency(p.rackRate, tenant.currency?.code)}
                    </option>
                  ))}
                </select>
              ) : null}
              <input
                value={item.description}
                onChange={(e) =>
                  updateLineItem(idx, "description", e.target.value)
                }
                placeholder="Description"
                style={{
                  ...inputStyle,
                  marginTop: products.length > 0 ? 4 : 0,
                }}
              />
            </div>
            <input
              type="number"
              value={item.amount}
              onChange={(e) => updateLineItem(idx, "amount", e.target.value)}
              placeholder="Price"
              style={{ ...inputStyle, width: 100 }}
            />
            <input
              type="number"
              value={item.quantity}
              onChange={(e) => updateLineItem(idx, "quantity", e.target.value)}
              min={1}
              style={{ ...inputStyle, width: 60 }}
            />
            {lineItems.length > 1 && (
              <button
                onClick={() => removeLineItem(idx)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: colors.textSecondary,
                }}
              >
                <X size={14} />
              </button>
            )}
          </div>
        ))}
        <button
          onClick={addLineItem}
          style={{
            ...btnSecondary,
            fontSize: 12,
            padding: "4px 12px",
            marginBottom: 16,
          }}
        >
          <Plus size={12} /> Add Item
        </button>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: 16,
            fontSize: 15,
            fontWeight: 700,
            color: colors.text,
          }}
        >
          Total: {fmtCurrency(totalAmount, tenant.currency?.code)}
        </div>

        {/* Due date & source */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
            marginBottom: 16,
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                fontSize: 12,
                fontWeight: 600,
                color: colors.textSecondary,
                marginBottom: 4,
              }}
            >
              Due Date
            </label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label
              style={{
                display: "block",
                fontSize: 12,
                fontWeight: 600,
                color: colors.textSecondary,
                marginBottom: 4,
              }}
            >
              Source
            </label>
            <select
              value={source}
              onChange={(e) => setSource(e.target.value)}
              style={{ ...inputStyle, cursor: "pointer" }}
            >
              <option value="manual">Salesperson</option>
              <option value="newsletter">Newsletter (CA)</option>
              <option value="portal">Portal</option>
            </select>
          </div>
        </div>

        {/* Notes */}
        <label
          style={{
            display: "block",
            fontSize: 12,
            fontWeight: 600,
            color: colors.textSecondary,
            marginBottom: 4,
          }}
        >
          Notes
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder="Internal notes…"
          style={{ ...inputStyle, resize: "vertical", marginBottom: 16 }}
        />

        {/* Options */}
        <div style={{ display: "flex", gap: 20, marginBottom: 20 }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              color: colors.text,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={syncToQBO}
              onChange={(e) => setSyncToQBO(e.target.checked)}
            />{" "}
            Sync to QuickBooks
          </label>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              color: colors.text,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={createStripeLink}
              onChange={(e) => setCreateStripeLink(e.target.checked)}
            />{" "}
            Stripe Payment Link
          </label>
        </div>

        {/* Submit */}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={btnSecondary} disabled={saving}>
            Cancel
          </button>
          <button onClick={handleSubmit} style={btnPrimary} disabled={saving}>
            {saving ? (
              <Loader2
                size={14}
                className="spin"
              />
            ) : (
              <Plus size={14} />
            )}
            Create Invoice
          </button>
        </div>
      </div>    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────

export default function Invoices() {
  const tenant = useTenant();
  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchInvoices({
        status: statusFilter || undefined,
        search: searchText || undefined,
      });
      setInvoices(data.invoices || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchText]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const handleAction = async (id, action) => {
    setActionLoading(id);
    setError(null);
    try {
      if (action === "send") await sendInvoice(id);
      else if (action === "mark-paid") await markInvoicePaid(id);
      else if (action === "void") await voidInvoice(id);
      setSuccess(
        `Invoice ${action === "send" ? "sent" : action === "mark-paid" ? "marked as paid" : "voided"} successfully`,
      );
      loadInvoices();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    setSuccess("Payment link copied to clipboard");
    setTimeout(() => setSuccess(null), 2000);
  };

  // Summary stats
  const stats = {
    total: invoices.length,
    draft: invoices.filter((i) => i.status === "draft").length,
    sent: invoices.filter((i) => i.status === "sent").length,
    paid: invoices.filter((i) => i.status === "paid").length,
    totalValue: invoices.reduce((s, i) => s + (i.totalAmount || 0), 0),
    paidValue: invoices
      .filter((i) => i.status === "paid")
      .reduce((s, i) => s + (i.totalAmount || 0), 0),
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <div>
          <h1
            style={{
              fontFamily: fonts.heading,
              fontSize: 28,
              fontWeight: 700,
              color: colors.text,
              marginBottom: 4,
            }}
          >
            Invoices
          </h1>
          <p
            style={{
              fontFamily: fonts.body,
              fontSize: 14,
              color: colors.textSecondary,
            }}
          >
            Create and manage invoices with QBO sync and Stripe payment links
          </p>
        </div>
        <button onClick={() => setShowCreate(true)} style={btnPrimary}>
          <Plus size={14} /> New Invoice
        </button>
      </div>

      {/* Summary cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
          gap: 12,
          marginBottom: 20,
        }}
      >
        {[
          {
            label: "Total",
            value: stats.total,
            icon: FileText,
            color: colors.text,
          },
          {
            label: "Draft",
            value: stats.draft,
            icon: FileText,
            color: colors.textSecondary,
          },
          { label: "Sent", value: stats.sent, icon: Send, color: "#3b82f6" },
          {
            label: "Paid",
            value: stats.paid,
            icon: CheckCircle2,
            color: colors.success,
          },
          {
            label: "Total Value",
            value: fmtCurrency(stats.totalValue, tenant.currency?.code),
            icon: DollarSign,
            color: colors.text,
          },
          {
            label: "Collected",
            value: fmtCurrency(stats.paidValue, tenant.currency?.code),
            icon: DollarSign,
            color: colors.success,
          },
        ].map((s) => (
          <div
            key={s.label}
            style={{ ...card, padding: 16, textAlign: "center" }}
          >
            <s.icon size={18} color={s.color} style={{ marginBottom: 4 }} />
            <div
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: s.color,
                fontFamily: fonts.heading,
              }}
            >
              {s.value}
            </div>
            <div style={{ fontSize: 11, color: colors.textSecondary }}>
              {s.label}
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div
        style={{
          display: "flex",
          gap: 10,
          marginBottom: 16,
          alignItems: "center",
        }}
      >
        <div style={{ position: "relative", flex: 1, maxWidth: 320 }}>
          <Search
            size={14}
            style={{
              position: "absolute",
              left: 10,
              top: 10,
              color: colors.textSecondary,
            }}
          />
          <input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search invoices…"
            style={{ ...inputStyle, paddingLeft: 30 }}
          />
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setStatusFilter(f.value)}
              style={{
                padding: "6px 12px",
                fontSize: 12,
                fontFamily: fonts.body,
                border: `1px solid ${statusFilter === f.value ? colors.accent : colors.border}`,
                borderRadius: 6,
                background:
                  statusFilter === f.value
                    ? `${colors.accent}15`
                    : "transparent",
                color:
                  statusFilter === f.value
                    ? colors.accent
                    : colors.textSecondary,
                cursor: "pointer",
                fontWeight: statusFilter === f.value ? 600 : 400,
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
        <button
          onClick={loadInvoices}
          style={{ ...btnSecondary, padding: "6px 10px" }}
          title="Refresh"
        >
          <RefreshCw size={14} />
        </button>
      </div>

      {error && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: 12,
            borderRadius: 6,
            background: `${colors.danger}15`,
            color: colors.danger,
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}
      {success && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: 12,
            borderRadius: 6,
            background: `${colors.success}15`,
            color: colors.success,
            fontSize: 12,
          }}
        >
          {success}
        </div>
      )}

      {/* Invoice list */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 60 }}>
          <Loader2
            size={24}
            color={colors.textSecondary}
            className="spin"
          />
        </div>
      ) : invoices.length === 0 ? (
        <div
          style={{
            ...card,
            textAlign: "center",
            padding: 60,
            color: colors.textSecondary,
          }}
        >
          <FileText
            size={32}
            color={colors.textSecondary}
            style={{ marginBottom: 8 }}
          />
          <div style={{ fontSize: 14 }}>No invoices found</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>
            Create your first invoice to get started
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {invoices.map((inv) => (
            <div
              key={inv.id}
              style={{
                ...card,
                padding: 16,
                display: "flex",
                alignItems: "center",
                gap: 16,
              }}
            >
              {/* Left: info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 4,
                  }}
                >
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: colors.text,
                    }}
                  >
                    {inv.invoiceNumber}
                  </span>
                  <StatusBadge status={inv.status} />
                  {inv.qboInvoiceId && (
                    <Badge color="#2CA01C" bg="rgba(44,160,28,0.1)">
                      QBO
                    </Badge>
                  )}
                  {inv.stripePaymentLink && (
                    <Badge color="#635bff" bg="rgba(99,91,255,0.1)">
                      Stripe
                    </Badge>
                  )}
                </div>
                <div style={{ fontSize: 13, color: colors.text }}>
                  {inv.contactName || inv.contactEmail}
                  {inv.companyName && (
                    <span style={{ color: colors.textSecondary }}>
                      {" "}
                      — {inv.companyName}
                    </span>
                  )}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: colors.textSecondary,
                    marginTop: 2,
                  }}
                >
                  {inv.items?.length || 0} item
                  {(inv.items?.length || 0) !== 1 ? "s" : ""} · Due{" "}
                  {fmtDate(inv.dueDate)}
                  {inv.sentAt && <span> · Sent {fmtDate(inv.sentAt)}</span>}
                  {inv.paidAt && <span> · Paid {fmtDate(inv.paidAt)}</span>}
                </div>
              </div>

              {/* Amount */}
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: colors.text,
                  fontFamily: fonts.heading,
                  minWidth: 90,
                  textAlign: "right",
                }}
              >
                {fmtCurrency(inv.totalAmount, inv.currency)}
              </div>

              {/* Actions */}
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                {inv.stripePaymentLink && (
                  <>
                    <button
                      onClick={() => copyLink(inv.stripePaymentLink)}
                      title="Copy payment link"
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: 4,
                        color: colors.textSecondary,
                      }}
                    >
                      <Copy size={14} />
                    </button>
                    <a
                      href={inv.stripePaymentLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open payment link"
                      style={{ color: colors.textSecondary, padding: 4 }}
                    >
                      <ExternalLink size={14} />
                    </a>
                  </>
                )}
                {inv.status === "draft" && (
                  <button
                    onClick={() => handleAction(inv.id, "send")}
                    disabled={actionLoading === inv.id}
                    title="Send invoice"
                    style={{ ...btnPrimary, padding: "5px 12px", fontSize: 12 }}
                  >
                    {actionLoading === inv.id ? (
                      <Loader2
                        size={12}
                        className="spin"
                      />
                    ) : (
                      <Send size={12} />
                    )}
                    Send
                  </button>
                )}
                {(inv.status === "sent" || inv.status === "draft") && (
                  <button
                    onClick={() => handleAction(inv.id, "mark-paid")}
                    disabled={actionLoading === inv.id}
                    title="Mark as paid"
                    style={{
                      ...btnSecondary,
                      padding: "5px 12px",
                      fontSize: 12,
                      color: colors.success,
                      borderColor: colors.success,
                    }}
                  >
                    {actionLoading === inv.id ? (
                      <Loader2
                        size={12}
                        className="spin"
                      />
                    ) : (
                      <CheckCircle2 size={12} />
                    )}
                    Paid
                  </button>
                )}
                {inv.status !== "void" && inv.status !== "paid" && (
                  <button
                    onClick={() => handleAction(inv.id, "void")}
                    disabled={actionLoading === inv.id}
                    title="Void invoice"
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 4,
                      color: colors.textSecondary,
                    }}
                  >
                    <Ban size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <CreateInvoiceModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(inv) => {
          setSuccess(`Invoice ${inv.invoiceNumber} created`);
          loadInvoices();
          setTimeout(() => setSuccess(null), 3000);
        }}
        tenant={tenant}
      />    </div>
  );
}
